import { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import { fetchAuditLog } from "@/services/superadminApi";
import {
    SectionHeader,
    TablePro,
    Badge,
    StatCard,
    CardGlass,
    Button
} from "@/components/ui/PremiumComponents";
import {
    Activity,
    RefreshCw,
    Filter,
    Clock,
    User,
    Search,
    ChevronLeft,
    ChevronRight,
    Shield
} from 'lucide-react';
import { toast } from "sonner";

const ACTION_COLOR_MAP = {
    create_tenant: "success",
    edit_tenant: "primary",
    suspend_tenant: "warning",
    reactivate_tenant: "success",
    delete_tenant: "danger",
    impersonate_start: "info",
    change_plan: "primary",
    change_subscription_status: "warning",
    set_feature_flags: "info",
    set_feature_override: "info",
    update_global_flags: "primary",
    create_plan: "success",
    update_plan: "primary",
    delete_plan: "danger",
    reset_demo: "warning",
    login: "success",
};

const ACTION_LABELS = {
    create_tenant: "Crear restaurante",
    edit_tenant: "Editar restaurante",
    suspend_tenant: "Suspender restaurante",
    reactivate_tenant: "Reactivar restaurante",
    delete_tenant: "Eliminar restaurante",
    impersonate_start: "Impersonación iniciada",
    change_plan: "Cambio de plan",
    change_subscription_status: "Cambio estado suscripción",
    set_feature_flags: "Feature flags actualizados",
    set_feature_override: "Override de feature",
    update_global_flags: "Flags globales actualizados",
    create_plan: "Plan creado",
    update_plan: "Plan editado",
    delete_plan: "Plan eliminado",
    reset_demo: "Reset datos demo",
    login: "Inicio de sesión",
};

export default function SuperAdminAuditoria() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [filterAction, setFilterAction] = useState("");
    const [filterCompany, setFilterCompany] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        setError("");
        const params = { page, limit: 50 };
        if (filterAction) params.action = filterAction;
        if (filterCompany) params.company_id = filterCompany;

        fetchAuditLog(params)
            .then(setData)
            .catch(() => {
                setError("No se pudo cargar el log de auditoría.");
                toast.error("Error al sincronizar auditoría");
            })
            .finally(() => setLoading(false));
    }, [page, filterAction, filterCompany]);

    useEffect(() => { load(); }, [load]);

    const items = data?.items || [];
    const total = data?.total || 0;
    const pages = Math.max(1, Math.ceil(total / 50));

    const renderRow = (ev) => (
        <>
            <td className="px-6 py-4">
                <Badge status={ACTION_COLOR_MAP[ev.action] || 'default'}>
                    {ACTION_LABELS[ev.action] || ev.action}
                </Badge>
            </td>
            <td className="px-6 py-4">
                <div className="flex flex-col">
                    <span className="text-sm font-bold text-white truncate max-w-[200px]">
                        {ev.target_company_id || "Sistema"}
                    </span>
                    <span className="text-[10px] font-mono text-white/30 truncate w-32">
                        {ev.audit_id || 'N/A'}
                    </span>
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <User size={12} className="text-primary" />
                    </div>
                    <span className="text-xs text-white/60 truncate max-w-[150px]">
                        {ev.superadmin_email || ev.superadmin_id || "—"}
                    </span>
                </div>
            </td>
            <td className="px-6 py-4">
                <span className="text-[10px] font-mono text-cyan-400/80 truncate block max-w-xs">
                    {ev.metadata ? JSON.stringify(ev.metadata) : "—"}
                </span>
            </td>
            <td className="px-6 py-4 text-right">
                <div className="flex flex-col items-end">
                    <span className="text-xs font-bold text-white">
                        {new Date(ev.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-[10px] text-white/40">
                        {new Date(ev.created_at).toLocaleTimeString()}
                    </span>
                </div>
            </td>
        </>
    );

    return (
        <SuperAdminLayout>
            <div className="space-y-8 page-fade-in">
                <SectionHeader
                    title="Auditoría de Eventos"
                    subtitle="Registro completo de todas las acciones realizadas desde el panel SuperAdmin."
                    actions={
                        <Button variant="secondary" onClick={load} className="flex items-center gap-2">
                            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
                        </Button>
                    }
                />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <StatCard label="Total Acciones" value={total} icon={<Activity size={22} />} />
                    <StatCard label="Restaurantes" value={items.length} icon={<Shield size={22} />} theme="info" />
                    <StatCard label="Sesiones Admin" value="Active" icon={<User size={22} />} />
                    <StatCard label="Letencia Logs" value="< 50ms" icon={<Clock size={22} />} theme="success" />
                </div>

                <CardGlass className="p-4">
                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="flex-1 relative group">
                            <Filter size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-primary transition-colors" />
                            <select
                                value={filterAction}
                                onChange={e => { setFilterAction(e.target.value); setPage(1); }}
                                className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-12 pr-4 text-sm text-white outline-none focus:ring-2 ring-primary/20 appearance-none cursor-pointer"
                            >
                                <option value="" className="bg-slate-900">Todos los tipos de acción</option>
                                {Object.entries(ACTION_LABELS).map(([k, label]) => (
                                    <option key={k} value={k} className="bg-slate-900">{label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-[2] relative group">
                            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-primary transition-colors" />
                            <input
                                type="text"
                                placeholder="Filtrar por ID de restaurante o palabra clave…"
                                value={filterCompany}
                                onChange={e => { setFilterCompany(e.target.value); setPage(1); }}
                                className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-12 pr-4 text-sm text-white outline-none focus:ring-2 ring-primary/20 transition-all font-medium"
                            />
                        </div>
                    </div>
                </CardGlass>

                <TablePro
                    headers={["Acción", "Restaurante / ID", "Administrador", "Metadatos", "Fecha / Hora"]}
                    data={items}
                    loading={loading}
                    renderRow={renderRow}
                    emptyText="No se encontraron eventos de auditoría registrados"
                />

                {/* Paginación Premium */}
                {!loading && pages > 1 && (
                    <div className="flex items-center justify-between pt-6">
                        <div className="text-xs text-white/40 font-bold">
                            MOSTRANDO PÁGINA {page} DE {pages} · {total} EVENTOS REGISTRADOS
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="!py-2 !px-4"
                            >
                                <ChevronLeft size={16} />
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => setPage(p => Math.min(pages, p + 1))}
                                disabled={page === pages}
                                className="!py-2 !px-4"
                            >
                                <ChevronRight size={16} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </SuperAdminLayout>
    );
}
